import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLanguageTheme } from "../../../../hooks/useLanguageTheme";
import { ProfileAffiliatesHeader } from "../../affiliates/layout/ProfileAffiliatesHeader";
import { AffiliatesMainContainer } from "../../affiliates/layout/AffiliatesMainContainer";
import { NotificationSnackBar } from "../../../campaignBuilder/NotificationSnackBar";
import { NotificationStatus } from "../../../campaignBuilder/content/steps/CampaignSetUpStep";
import { useSelector } from "react-redux";
import {
  linkCopiedOpenSelector,
  QRCodeCopiedOpenSelector,
  setLinkCopiedClose,
  setQRCodeCopiedClose,
} from "../../../../store/features/profileSlice";
import { useAppDispatch } from "../../../../store/store";
import {
  IDateRange,
  IGetAffiliateProfileParams,
  IGetAffiliatesProfileParamsWithLang,
  useGetAffiliatesProfileQuery,
} from "../../../../store/api/affiliatesProfileApiSlice";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  buildSearchParamsFromGetPropertiesParams,
  parseSearchParams,
} from "../../affiliates/logic";
import { DEFAULT_USER_ID } from "../../../portfolio/constants";
import { useSearchParams } from "react-router-dom";
import { ISortParams } from "../../../portfolio/interfaces";
import {
  DEFAULT_DATE_RANGE,
  DEFAULT_LEAD_STATUS,
  DEFAULT_SORT,
} from "../../affiliates/contants";
import { useMediaQuery } from "@mui/material";
import { ProfileAffiliatesHeaderMobile } from "../../affiliates/layout/ProfileAffiliatesHeaderMobile";
import { AffiliateLinkFixedBtn } from "../../affiliates/elements/AffiliateLinkFixedBtn";
import { AffiliatesLinkMobileSidebar } from "../../affiliates/layout/AffiliatesLinkMobileSidebar";
import { AffiliatesFilterMobileSidebar } from "../../affiliates/layout/AffiliatesFilterMobileSidebar";

export const AffiliatesMainContent = ({
  sidebarWidth,
}: {
  sidebarWidth: number;
}) => {
  const { i18n, t } = useTranslation(["profile"]);
  const dispatch = useAppDispatch();
  const langTheme = useLanguageTheme();
  const linkCopiedOpen = useSelector(linkCopiedOpenSelector);
  const QRCopiedCopiedOpen = useSelector(QRCodeCopiedOpenSelector);
  const is970px = useMediaQuery("(max-width: 970px)");
  const is720px = useMediaQuery("(max-width: 720px)");

  const [_, setSearchParams] = useSearchParams();

  const [leadStatus, setLeadStatus] = React.useState(DEFAULT_LEAD_STATUS);
  const [sort, setSort] = useState<ISortParams>(DEFAULT_SORT);
  const [dateRange, setDateRange] =
    React.useState<IDateRange>(DEFAULT_DATE_RANGE);

  const user = localStorage.getItem("user") || sessionStorage.getItem("user");
  const [userId, setUserId] = React.useState(
    user ? JSON.parse(user).id : DEFAULT_USER_ID
  );

  const storedJwt =
    localStorage.getItem("jwt") || sessionStorage.getItem("jwt");
  const jwt = storedJwt ? storedJwt.replace(/"/g, "") : "";

  // Workaround for not setting state into URL before parsing
  const [initComplete, setInitComplete] = useState(false);

  // Sync/persist state back to Search Params
  useEffect(() => {
    if (!initComplete) return;

    const newParams = buildSearchParamsFromGetPropertiesParams({
      sort,
      leadStatus,
      userId,
      dateRange,
    });

    newParams.set("tab", "affiliates");

    setSearchParams(newParams);
  }, [initComplete, sort, leadStatus, userId, dateRange]);

  const enrichParams = async (
    params: IGetAffiliateProfileParams
  ): Promise<IGetAffiliateProfileParams> => {
    return params;
  };
  // Init page state from Search Params
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const params = parseSearchParams(searchParams, userId);

    enrichParams(params).then((params) => {
      setSort(params.sort);
      setLeadStatus(params.leadStatus);
      setUserId(params.userId);
      setDateRange(params.dateRange);
      setInitComplete(true);
    });
  }, []);

  const getAffiliatesProfileParams =
    useMemo<IGetAffiliatesProfileParamsWithLang | null>(() => {
      if (!userId) return null;
      return {
        params: {
          leadStatus: leadStatus,
          userId: userId,
          jwt: jwt,
          sort: sort,
          dateRange: dateRange,
        },
        lang: i18n.language,
      };
    }, [userId, jwt, leadStatus, i18n.language, dateRange, sort]);

  const { data: affiliatesProfileData } = useGetAffiliatesProfileQuery(
    getAffiliatesProfileParams ?? skipToken
  );

  return (
    <div
      style={{
        fontFamily: langTheme.mainFont(),
        direction: i18n.dir(),
      }}
    >
      {is970px ? (
        <ProfileAffiliatesHeaderMobile />
      ) : (
        <ProfileAffiliatesHeader
          initLeadStatus={leadStatus}
          onLeadStatusChange={setLeadStatus}
          dateRange={dateRange}
          setDateRange={setDateRange}
        />
      )}

      <AffiliatesMainContainer
        sidebarWidth={sidebarWidth}
        data={affiliatesProfileData?.data}
        setSort={setSort}
      />

      {is720px && <AffiliateLinkFixedBtn />}
      <AffiliatesLinkMobileSidebar />
      <AffiliatesFilterMobileSidebar
        initLeadStatus={leadStatus}
        onLeadStatusChange={setLeadStatus}
        dateRange={dateRange}
        setDateRange={setDateRange}
      />

      <NotificationSnackBar
        open={linkCopiedOpen}
        onClose={() => dispatch(setLinkCopiedClose())}
        type={NotificationStatus.SUCCESSFULL}
        snackbarText={t("link_copied")}
      />
      <NotificationSnackBar
        open={QRCopiedCopiedOpen}
        onClose={() => dispatch(setQRCodeCopiedClose())}
        type={NotificationStatus.SUCCESSFULL}
        snackbarText={t("qrcode_copied")}
      />
    </div>
  );
};
